import React, { useState, useEffect } from 'react';
import { XMarkIcon, UsersIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { listShares, type TrackShare } from '../services/shareService';
import { type UserSearchResult } from '../services/shareService';
import { logError, logInfo } from '../services/logtail';
import { useAuth } from '../contexts/AuthContext';

interface ShareInfoPopupProps {
  trackId: string;
  trackName: string;
  isOwner: boolean;
  ownerUserId?: string;
  ownerEmail?: string; // Pass owner email if available
  onClose: () => void;
  onUncollaborateFromShareInfo?: () => void; // Callback to refresh tracks after uncollaboration from ShareInfo popup
}

const ShareInfoPopup: React.FC<ShareInfoPopupProps> = ({ trackId, trackName, isOwner, ownerUserId, ownerEmail, onClose, onUncollaborateFromShareInfo }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [shares, setShares] = useState<TrackShare[]>([]);
  const [collaboratorUsers, setCollaboratorUsers] = useState<Record<string, UserSearchResult>>({});
  const [ownerUser, setOwnerUser] = useState<UserSearchResult | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to get initials from email
  const getInitials = (email: string): string => {
    if (!email) return '?';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  // Helper function to extract name from email
  const getNameFromEmail = (email: string): string => {
    if (!email) return '';
    const localPart = email.split('@')[0];
    // Convert dots, underscores, and hyphens to spaces and capitalize
    const name = localPart
      .replace(/[._-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    return name;
  };

  useEffect(() => {
    loadShareInfo();
  }, [trackId]);

  // Check if current user is a collaborator (not owner)
  const currentUserShare = shares.find(share => share.collaboratorUserId === user?.uid);
  const isCollaborator = !isOwner && currentUserShare !== undefined;

  const loadShareInfo = async () => {
    try {
      const sharesData = await listShares(trackId);
      setShares(sharesData);

      // Extract collaborator details from API response (already included)
      const usersMap: Record<string, UserSearchResult> = {};
      sharesData.forEach(share => {
        if (share.collaborator && share.collaboratorUserId) {
          usersMap[share.collaboratorUserId] = share.collaborator;
        }
      });
      setCollaboratorUsers(usersMap);

      // Extract owner details from API response (owner info is already included)
      if (sharesData.length > 0 && sharesData[0].owner) {
        setOwnerUser(sharesData[0].owner);
      } else if (isOwner && user) {
        // If user is the owner and no shares exist, use current user as owner
        setOwnerUser({
          id: user.uid,
          userId: user.uid,
          email: user.email || '',
          effectiveTier: 'starter', // Will be updated if owner is subscribed
        });
      }

    } catch (error) {
      logError('Failed to load share info', { trackId, error });
    } finally {
      setLoading(false);
    }
  };

  const handleUncollaborateFromShareInfo = () => {
    if (!user) return;
    
    logInfo('User attempted to uncollaborate from ShareInfo popup', { trackId, trackName, userId: user.uid });
    
    // Just call the callback - Dashboard will handle the confirmation and uncollaboration
    onUncollaborateFromShareInfo && onUncollaborateFromShareInfo();
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[var(--color-surface)] rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--collab-blue-bg)' }}>
              <UsersIcon className="w-4 h-4" style={{ color: 'var(--collab-blue-text)' }} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[var(--color-text)]">
                {t('share.sharingInfo')}
              </h2>
              <p className="text-sm text-[var(--color-muted)] truncate max-w-[200px]" title={trackName}>
                {trackName}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-[var(--color-muted)] hover:text-[var(--color-text)] transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--brand-from)' }}></div>
              <p className="text-sm text-[var(--color-muted)]">Loading sharing info...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Owner Info */}
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  {t('share.owner')}
                </h3>
                {ownerUser ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--info-green-bg)', borderColor: 'var(--info-green-border)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, var(--brand-from), var(--brand-to))' }}>
                      {getInitials(ownerUser.email)}
                    </div>
                    <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col">
                                <span className="font-medium text-[var(--color-text)] truncate">
                                  {isOwner ? t('share.you') : getNameFromEmail(ownerUser.email)}
                                </span>
                                <span className="text-xs text-[var(--color-muted)] truncate">
                                  {isOwner ? t('share.youOwnThisTrack') : ownerUser.email}
                                </span>
                              </div>
                              {(ownerUser.effectiveTier === 'artist' || ownerUser.effectiveTier === 'studio') && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded" style={{ backgroundColor: 'var(--info-amber-bg)', color: 'var(--info-amber-text)' }}>
                                  {ownerUser.effectiveTier === 'studio' ? 'Studio' : 'Artist'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-[var(--color-muted)] truncate font-mono">{ownerUser.userId}</span>
                            </div>
                    </div>
                  </div>
                ) : ownerEmail ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--info-green-bg)', borderColor: 'var(--info-green-border)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, var(--brand-from), var(--brand-to))' }}>
                      {getInitials(ownerEmail)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--color-text)] truncate">
                          {isOwner ? t('share.you') : ownerEmail}
                        </span>
                      </div>
                      {ownerUserId && (
                        <div className="text-xs text-[var(--color-muted)] truncate font-mono">{ownerUserId}</div>
                      )}
                    </div>
                  </div>
                ) : ownerUserId ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--info-green-bg)', borderColor: 'var(--info-green-border)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, var(--brand-from), var(--brand-to))' }}>
                      ?
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--color-text)] truncate">
                          {isOwner ? t('share.you') : t('share.owner')}
                        </span>
                      </div>
                      <div className="text-xs text-[var(--color-muted)] truncate font-mono">{ownerUserId}</div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
                    <p className="text-sm text-[var(--color-muted)]">
                      {isOwner ? t('share.youOwnThisTrack') : t('share.ownerInfoUnavailable')}
                    </p>
                  </div>
                )}
              </div>

              {/* Collaborators */}
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--collab-blue-text)' }}></div>
                  {t('share.collaborators')} ({shares.length})
                </h3>
                
                {shares.length === 0 ? (
                  <div className="p-3 rounded-lg border" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
                    <p className="text-sm text-[var(--color-muted)]">
                      {t('share.noCollaborators')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {shares.map((share) => {
                      const user = collaboratorUsers[share.collaboratorUserId];
                      
                      return (
                        <div
                          key={share.collaboratorUserId}
                          className="flex items-center gap-3 p-3 bg-[var(--color-bg)] rounded-lg border border-[var(--color-border)]"
                        >
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, var(--brand-from), var(--brand-to))' }}>
                            {user ? getInitials(user.email) : '?'}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col">
                                <span className="font-medium text-[var(--color-text)] truncate text-sm">
                                  {user ? getNameFromEmail(user.email) : share.collaboratorUserId}
                                </span>
                                <span className="text-xs text-[var(--color-muted)] truncate">
                                  {user ? user.email : ''}
                                </span>
                              </div>
                              {(user?.effectiveTier === 'artist' || user?.effectiveTier === 'studio') && (
                                <span className="px-1.5 py-0.5 text-xs font-medium rounded" style={{ backgroundColor: 'var(--info-amber-bg)', color: 'var(--info-amber-text)' }}>
                                  {user.effectiveTier === 'studio' ? 'Studio' : 'Artist'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs text-[var(--color-muted)] truncate font-mono">
                                {user ? user.userId : share.collaboratorUserId}
                              </span>
                              <span
                                className="text-xs px-2 py-0.5 rounded"
                                style={{
                                  backgroundColor: share.role === 'editor' ? 'var(--info-amber-bg)' : 'var(--collab-blue-bg)',
                                  color: share.role === 'editor' ? 'var(--info-amber-text)' : 'var(--collab-blue-text)'
                                }}
                              >
                                {share.role === 'editor' ? t('share.editor') : t('share.viewer')}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Uncollaborate Button for Collaborators */}
              {isCollaborator && (
                <div className="pt-4 border-t border-[var(--color-border)]">
                  <button
                    onClick={handleUncollaborateFromShareInfo}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white font-medium rounded-lg transition-colors"
                    style={{
                      backgroundColor: '#ef4444'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ef4444';
                    }}
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <line x1="22" y1="2" x2="17" y2="7"/>
                      <line x1="17" y1="2" x2="22" y2="7"/>
                    </svg>
                    {t('dashboard.uncollaborate') || 'Leave Collaboration'}
                  </button>
                  <p className="text-xs text-[var(--color-muted)] text-center mt-2">
                    {t('dashboard.uncollaborateDescription') || 'You will no longer have access to this track.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareInfoPopup;
